export const SEEK_TIMEOUT = 7_000;

export type PlayerVideoSourceType = null | 'hls' | 'standard';
export type RangeProbeStatus = 'unknown' | 'checking' | 'supported' | 'unsupported' | 'not-applicable' | 'error';
export type SeekRequestSource = 'jw-ui' | 'external-command' | 'initial-starttime';
export type SeekDiagnosticStatus = 'idle' | 'pending' | 'succeeded' | 'failed';
export type PlayerCommandType = 'play' | 'pause' | 'seek';
export type PlayerCommandStatus = 'idle' | 'requested' | 'succeeded' | 'failed';
export type PlayerPlaybackState = 'idle' | 'playing' | 'paused' | 'buffering' | 'complete' | 'error';
export type PlayerDiagnosticsInterpretationTone = 'neutral' | 'success' | 'warning' | 'error';

export interface RangeProbeDiagnostics {
    status: RangeProbeStatus;
    url: string;
    sourceType: PlayerVideoSourceType;
    responseStatus?: number;
    acceptRanges: string | null;
    contentRange: string | null;
    contentLength: string | null;
    checkedAt: string | null;
    error: string | null;
}

export interface TimeRangeSnapshot {
    start: number | null;
    end: number | null;
    length: number;
}

export interface NativeMediaErrorDiagnostics {
    code: number | null;
    message: string | null;
    capturedAt: string | null;
}

export interface BrowserMediaDiagnostics {
    currentSrc: string | null;
    readyState: number | null;
    networkState: number | null;
    paused: boolean | null;
    ended: boolean | null;
    seekable: TimeRangeSnapshot;
    buffered: TimeRangeSnapshot;
    nativeError: NativeMediaErrorDiagnostics;
    lastUpdatedAt: string | null;
}

export interface SeekDiagnostics {
    status: SeekDiagnosticStatus;
    source: SeekRequestSource | null;
    targetPosition: number | null;
    startingPosition: number | null;
    requestedAt: string | null;
    resolvedAt: string | null;
    observedPosition: number | null;
    failureReason: string | null;
}

export interface PlayerCommandDiagnostics {
    command: PlayerCommandType | null;
    status: PlayerCommandStatus;
    requestedAt: string | null;
    resolvedAt: string | null;
    details: string | null;
}

export interface PlayerDiagnosticsState {
    currentFile: string;
    currentPosition: number;
    currentDuration: number;
    playbackState: PlayerPlaybackState;
    browserMedia: BrowserMediaDiagnostics;
    rangeProbe: RangeProbeDiagnostics;
    lastSeek: SeekDiagnostics;
    lastPlayPauseCommand: PlayerCommandDiagnostics;
    lastCommand: PlayerCommandDiagnostics;
}

export interface PlayerDiagnosticsInterpretation {
    id: string;
    title: string;
    message: string;
    tone: PlayerDiagnosticsInterpretationTone;
}

export const createInitialRangeProbeDiagnostics = (
    sourceType: PlayerVideoSourceType = null,
    url = ''
): RangeProbeDiagnostics => ({
    status: 'unknown',
    url,
    sourceType,
    acceptRanges: null,
    contentRange: null,
    contentLength: null,
    checkedAt: null,
    error: null,
});

export const createInitialTimeRangeSnapshot = (): TimeRangeSnapshot => ({
    start: null,
    end: null,
    length: 0,
});

export const createInitialNativeMediaErrorDiagnostics = (): NativeMediaErrorDiagnostics => ({
    code: null,
    message: null,
    capturedAt: null,
});

export const createInitialBrowserMediaDiagnostics = (): BrowserMediaDiagnostics => ({
    currentSrc: null,
    readyState: null,
    networkState: null,
    paused: null,
    ended: null,
    seekable: createInitialTimeRangeSnapshot(),
    buffered: createInitialTimeRangeSnapshot(),
    nativeError: createInitialNativeMediaErrorDiagnostics(),
    lastUpdatedAt: null,
});

export const createInitialSeekDiagnostics = (): SeekDiagnostics => ({
    status: 'idle',
    source: null,
    targetPosition: null,
    startingPosition: null,
    requestedAt: null,
    resolvedAt: null,
    observedPosition: null,
    failureReason: null,
});

export const createInitialPlayerCommandDiagnostics = (): PlayerCommandDiagnostics => ({
    command: null,
    status: 'idle',
    requestedAt: null,
    resolvedAt: null,
    details: null,
});

export const createInitialPlayerDiagnosticsState = (): PlayerDiagnosticsState => ({
    currentFile: '',
    currentPosition: 0,
    currentDuration: 0,
    playbackState: 'idle',
    browserMedia: createInitialBrowserMediaDiagnostics(),
    rangeProbe: createInitialRangeProbeDiagnostics(),
    lastSeek: createInitialSeekDiagnostics(),
    lastPlayPauseCommand: createInitialPlayerCommandDiagnostics(),
    lastCommand: createInitialPlayerCommandDiagnostics(),
});

export const getSeekToleranceSeconds = (
    targetPosition: number,
    startingPosition: number | null = null
) => {
    const baseline = startingPosition ?? 0;
    const delta = Math.abs(targetPosition - baseline);

    return Math.max(5, Math.min(15, Math.round(delta * 0.1) || 5));
};

export const didSeekReachTarget = ({
    targetPosition,
    observedPosition,
    startingPosition = null,
}: {
    targetPosition: number;
    observedPosition: number;
    startingPosition?: number | null;
}) => {
    return Math.abs(observedPosition - targetPosition) <=
        getSeekToleranceSeconds(targetPosition, startingPosition);
};

export const getLikelySeekFailureReason = ({
    rangeProbe,
    targetPosition,
    observedPosition,
}: {
    rangeProbe: RangeProbeDiagnostics;
    targetPosition: number;
    observedPosition: number;
}) => {
    if (rangeProbe.sourceType === 'standard' && rangeProbe.status === 'unsupported') {
        return 'Byte-range requests appear unsupported for the current MP4 source.';
    }

    if (rangeProbe.sourceType === 'standard' && rangeProbe.status === 'error') {
        return 'The browser could not verify byte-range support for the current MP4 source.';
    }

    if (rangeProbe.sourceType === 'hls' && rangeProbe.status === 'unsupported') {
        return 'Byte-range requests appear unsupported for the HLS media assets referenced by the current playlist.';
    }

    if (rangeProbe.sourceType === 'hls' && rangeProbe.status === 'error') {
        return 'The browser could not verify byte-range support for the HLS media assets referenced by the current playlist.';
    }

    return `The player stayed near ${Math.round(observedPosition)}s instead of reaching ${Math.round(targetPosition)}s.`;
};

const getRangeProbeResponse = async (
    url: string,
    sourceType: PlayerVideoSourceType,
    checkedAt: string,
    messageOverride: string | null = null
): Promise<RangeProbeDiagnostics> => {
    const abortController = new AbortController();
    const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        headers: {
            Range: 'bytes=0-1',
        },
        signal: abortController.signal,
    });

    const responseStatus = response.status;
    const contentRange = response.headers.get('content-range');
    const acceptRanges = response.headers.get('accept-ranges');
    const contentLength = response.headers.get('content-length');
    const isSupported = responseStatus === 206;

    abortController.abort();

    if (response.body) {
        void response.body.cancel().catch(() => undefined);
    }

    return {
        status: isSupported ? 'supported' : 'unsupported',
        url,
        sourceType,
        responseStatus,
        acceptRanges,
        contentRange,
        contentLength,
        checkedAt,
        error: messageOverride ?? (
            isSupported
                ? null
                : 'Expected a 206 Partial Content response.'
        ),
    };
};

const getHlsPlaylistLines = (playlistContents: string) => {
    return playlistContents
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
};

const resolveUrl = (baseUrl: string, url: string) => {
    return new URL(url, baseUrl).toString();
};

const getQuotedAttributeValue = (line: string, attributeName: string) => {
    const attributeMatch = line.match(new RegExp(`${attributeName}="([^"]+)"`));

    return attributeMatch?.[1] ?? null;
};

const fetchPlaylistContents = async (playlistUrl: string) => {
    const playlistResponse = await fetch(playlistUrl, {
        method: 'GET',
        cache: 'no-store',
    });

    if (!playlistResponse.ok) {
        throw new Error(`Failed to load HLS playlist (${playlistResponse.status}).`);
    }

    return playlistResponse.text();
};

type HlsByteRangeProbePlan =
    | {
        status: 'not-applicable';
        message: string;
        targetUrl: null;
    }
    | {
        status: 'probe-media';
        message: string;
        targetUrl: string;
    };

const getFirstVariantPlaylistUrl = (playlistLines: string[], playlistUrl: string) => {
    for (let index = 0; index < playlistLines.length; index += 1) {
        const line = playlistLines[index];

        if (line.startsWith('#EXT-X-I-FRAME-STREAM-INF')) {
            const iframeUrl = getQuotedAttributeValue(line, 'URI');

            if (iframeUrl) {
                return resolveUrl(playlistUrl, iframeUrl);
            }
        }

        if (!line.startsWith('#EXT-X-STREAM-INF')) {
            continue;
        }

        for (let nestedIndex = index + 1; nestedIndex < playlistLines.length; nestedIndex += 1) {
            const nestedLine = playlistLines[nestedIndex];

            if (nestedLine.startsWith('#')) {
                continue;
            }

            return resolveUrl(playlistUrl, nestedLine);
        }
    }

    return null;
};

const getFirstByteRangeMediaTargetUrl = (playlistLines: string[], playlistUrl: string) => {
    let nextSegmentUsesByteRange = false;

    for (const line of playlistLines) {
        if (line.startsWith('#EXT-X-MAP')) {
            const mapUrl = getQuotedAttributeValue(line, 'URI');

            if (mapUrl && line.includes('BYTERANGE=')) {
                return resolveUrl(playlistUrl, mapUrl);
            }
        }

        if (line.startsWith('#EXT-X-BYTERANGE')) {
            nextSegmentUsesByteRange = true;
            continue;
        }

        if (!nextSegmentUsesByteRange || line.startsWith('#')) {
            continue;
        }

        return resolveUrl(playlistUrl, line);
    }

    return null;
};

const getHlsByteRangeProbePlan = async (playlistUrl: string): Promise<HlsByteRangeProbePlan> => {
    const playlistContents = await fetchPlaylistContents(playlistUrl);
    const playlistLines = getHlsPlaylistLines(playlistContents);

    if (!playlistLines.some((line) => line.startsWith('#EXTM3U'))) {
        throw new Error('The HLS playlist response was not recognized as a valid m3u8 playlist.');
    }

    const variantPlaylistUrl = getFirstVariantPlaylistUrl(playlistLines, playlistUrl);

    if (variantPlaylistUrl) {
        return getHlsByteRangeProbePlan(variantPlaylistUrl);
    }

    const byteRangeMediaTargetUrl = getFirstByteRangeMediaTargetUrl(playlistLines, playlistUrl);

    if (!byteRangeMediaTargetUrl) {
        return {
            status: 'not-applicable' as const,
            message: 'The HLS playlist did not indicate byte-range-addressed media assets to probe.',
            targetUrl: null,
        };
    }

    return {
        status: 'probe-media' as const,
        message: 'The HLS playlist indicates byte-range-addressed media. The probe checked the first referenced media asset.',
        targetUrl: byteRangeMediaTargetUrl,
    };
};

export const probeMediaRangeSupport = async (
    url: string,
    sourceType: PlayerVideoSourceType
): Promise<RangeProbeDiagnostics> => {
    const checkedAt = new Date().toISOString();

    if (!url) {
        return {
            ...createInitialRangeProbeDiagnostics(sourceType, url),
            checkedAt,
            error: 'No current media URL is available for probing.',
        };
    }

    try {
        if (sourceType === 'hls') {
            const hlsProbePlan = await getHlsByteRangeProbePlan(url);

            if (hlsProbePlan.status === 'not-applicable') {
                return {
                    ...createInitialRangeProbeDiagnostics(sourceType, url),
                    checkedAt,
                    status: 'not-applicable',
                    error: hlsProbePlan.message,
                };
            }

            const hlsRangeProbeResult = await getRangeProbeResponse(
                hlsProbePlan.targetUrl,
                sourceType,
                checkedAt,
                hlsProbePlan.message
            );

            return {
                ...hlsRangeProbeResult,
                url,
            };
        }

        return getRangeProbeResponse(url, sourceType, checkedAt);
    } catch (error) {
        return {
            ...createInitialRangeProbeDiagnostics(sourceType, url),
            checkedAt,
            status: 'error',
            error: error instanceof Error
                ? error.message
                : 'Unknown error while probing byte-range support.',
        };
    }
};

const hasLimitedMp4Seekability = (diagnostics: PlayerDiagnosticsState) => {
    const seekableEnd = diagnostics.browserMedia.seekable.end;
    const bufferedEnd = diagnostics.browserMedia.buffered.end;
    const targetPosition = diagnostics.lastSeek.targetPosition;
    const currentDuration = diagnostics.currentDuration;

    if (
        seekableEnd === null ||
        bufferedEnd === null ||
        targetPosition === null ||
        diagnostics.browserMedia.seekable.length === 0 ||
        diagnostics.browserMedia.buffered.length === 0
    ) {
        return false;
    }

    const seekableCloselyTracksBuffered = Math.abs(seekableEnd - bufferedEnd) <= 30;
    const targetIsBeyondSeekable = targetPosition > seekableEnd + 15;
    const seekableDoesNotSpanFullMedia = currentDuration <= 0 || seekableEnd < currentDuration - 15;

    return seekableCloselyTracksBuffered && targetIsBeyondSeekable && seekableDoesNotSpanFullMedia;
};

export const getPlayerDiagnosticsInterpretations = ({
    diagnostics,
    videoSourceType,
}: {
    diagnostics: PlayerDiagnosticsState;
    videoSourceType?: PlayerVideoSourceType;
}): PlayerDiagnosticsInterpretation[] => {
    const interpretations: PlayerDiagnosticsInterpretation[] = [];
    const resolvedSourceType = videoSourceType ?? diagnostics.rangeProbe.sourceType;
    const hasFailedSeek = diagnostics.lastSeek.status === 'failed';
    const hasNativeBrowserMediaError = diagnostics.browserMedia.nativeError.code !== null;
    const isHlsByteRangeMediaProbe =
        resolvedSourceType === 'hls' &&
        diagnostics.rangeProbe.status !== 'not-applicable' &&
        diagnostics.rangeProbe.checkedAt !== null;
    const mp4LooksNonSeekableFromBrowserSignals =
        resolvedSourceType === 'standard' &&
        hasFailedSeek &&
        hasLimitedMp4Seekability(diagnostics);
    const hasSupportedRangeProbe =
        (resolvedSourceType === 'standard' || isHlsByteRangeMediaProbe) &&
        diagnostics.rangeProbe.status === 'supported' &&
        diagnostics.rangeProbe.responseStatus === 206;
    const hasFailedRangeProbe =
        (resolvedSourceType === 'standard' || isHlsByteRangeMediaProbe) &&
        (
            diagnostics.rangeProbe.status === 'unsupported' ||
            diagnostics.rangeProbe.status === 'error' ||
            (
                diagnostics.rangeProbe.responseStatus !== undefined &&
                diagnostics.rangeProbe.responseStatus !== 206 &&
                diagnostics.rangeProbe.status !== 'supported'
            )
        );

    if (hasNativeBrowserMediaError) {
        interpretations.push({
            id: 'native-browser-media-error',
            title: 'Browser media error reported',
            message: `The browser reported a native media error (${diagnostics.browserMedia.nativeError.code}). This may indicate a network, decoding, or source-level limitation in the current environment.`,
            tone: 'error',
        });
    }

    if (hasFailedSeek && hasFailedRangeProbe && resolvedSourceType === 'standard') {
        interpretations.push({
            id: 'blocked-mp4-range',
            title: 'Likely blocked MP4 byte-range support',
            message: 'The current results are consistent with an environment that is blocking or stripping MP4 byte-range requests. Ask the team managing the user\'s network, proxy, firewall, antivirus, or device security controls to confirm that HTTP Range requests and 206 Partial Content responses are allowed for download.pli.edu.',
            tone: 'error',
        });
    } else if (hasFailedSeek && hasFailedRangeProbe && resolvedSourceType === 'hls') {
        interpretations.push({
            id: 'blocked-hls-byte-range-media',
            title: 'Likely blocked HLS byte-range media support',
            message: 'The current HLS playlist appears to reference byte-range-addressed media, and the probe did not receive the expected HTTP 206 Partial Content response. Ask the team managing the user\'s network, proxy, firewall, antivirus, or device security controls to confirm that HTTP Range requests and 206 Partial Content responses are allowed for download.pli.edu.',
            tone: 'error',
        });
    } else if (hasFailedRangeProbe && resolvedSourceType === 'standard') {
        interpretations.push({
            id: 'range-probe-unsupported',
            title: 'Range probe indicates MP4 support is unavailable',
            message: 'The MP4 range probe did not receive the expected HTTP 206 Partial Content response. Seek and resume may not work correctly in this environment.',
            tone: 'error',
        });
    } else if (hasFailedRangeProbe && resolvedSourceType === 'hls') {
        interpretations.push({
            id: 'hls-range-probe-unsupported',
            title: 'Range probe indicates HLS byte-range media support is unavailable',
            message: 'The HLS playlist appears to reference byte-range-addressed media, but the probe did not receive the expected HTTP 206 Partial Content response. Seek and resume may not work correctly in this environment.',
            tone: 'error',
        });
    }

    if (mp4LooksNonSeekableFromBrowserSignals) {
        interpretations.push({
            id: 'limited-mp4-seekability',
            title: 'Browser signals suggest limited MP4 seekability',
            message: 'The browser appears able to seek only within media that is already buffered. This is consistent with blocked or degraded byte-range behavior for the current MP4 source.',
            tone: hasFailedRangeProbe ? 'error' : 'warning',
        });
    }

    if (hasFailedSeek && diagnostics.lastSeek.source === 'initial-starttime') {
        interpretations.push({
            id: 'resume-seek-failed',
            title: 'Resume from saved position failed',
            message: 'Watch attempted to resume from the saved position, but playback did not reach that position.',
            tone: 'error',
        });
    }

    if (hasFailedSeek && diagnostics.lastSeek.source === 'jw-ui') {
        interpretations.push({
            id: 'manual-seek-failed',
            title: 'Manual seek failed',
            message: 'The user scrubbed in the player UI, but playback did not reach the requested position.',
            tone: 'error',
        });
    }

    if (hasFailedSeek && diagnostics.lastSeek.source === 'external-command') {
        interpretations.push({
            id: 'app-seek-failed',
            title: 'App-issued seek failed',
            message: 'A Watch-issued seek command did not complete successfully.',
            tone: 'warning',
        });
    }

    if (hasFailedSeek && diagnostics.browserMedia.readyState !== null && diagnostics.browserMedia.readyState <= 1) {
        interpretations.push({
            id: 'browser-not-ready-to-seek',
            title: 'Browser was not ready to seek',
            message: 'The browser had not loaded enough media metadata or data to complete the seek when it was attempted.',
            tone: 'warning',
        });
    }

    if (
        hasFailedSeek &&
        !hasNativeBrowserMediaError &&
        diagnostics.browserMedia.networkState === 2
    ) {
        interpretations.push({
            id: 'browser-still-loading-during-seek',
            title: 'Browser was still loading media during the failed seek',
            message: 'The browser was actively loading media during the failed seek. This can indicate a slow or restricted environment rather than a definitive player defect.',
            tone: 'warning',
        });
    }

    if (resolvedSourceType === 'hls' && diagnostics.rangeProbe.status === 'not-applicable') {
        interpretations.push({
            id: 'hls-not-applicable',
            title: 'HLS playlist did not indicate byte-range media',
            message: 'This playback session uses HLS, and the inspected playlist did not indicate byte-range-addressed media assets to probe.',
            tone: 'neutral',
        });
    }

    if (hasSupportedRangeProbe && diagnostics.lastSeek.status === 'succeeded' && resolvedSourceType === 'standard') {
        interpretations.push({
            id: 'healthy-mp4-seek',
            title: 'MP4 seek path looks healthy',
            message: 'The current MP4 source returned HTTP 206 and the last seek succeeded in this environment.',
            tone: 'success',
        });
    }

    if (hasSupportedRangeProbe && diagnostics.lastSeek.status === 'succeeded' && resolvedSourceType === 'hls') {
        interpretations.push({
            id: 'healthy-hls-byte-range-media',
            title: 'HLS byte-range media path looks healthy',
            message: 'The HLS playlist appears to reference byte-range-addressed media, the probe returned HTTP 206, and the last seek succeeded in this environment.',
            tone: 'success',
        });
    }

    if (diagnostics.playbackState === 'error') {
        interpretations.push({
            id: 'playback-error',
            title: 'Playback error reported',
            message: 'The player is currently in an error state. Export the diagnostics JSON for engineering review.',
            tone: 'error',
        });
    }

    const hasMixedOrInconclusiveResults =
        (hasSupportedRangeProbe && hasFailedSeek) ||
        (hasSupportedRangeProbe && diagnostics.lastPlayPauseCommand.status === 'failed');

    if (hasMixedOrInconclusiveResults) {
        interpretations.push({
            id: 'mixed-results',
            title: 'Mixed or inconclusive results',
            message: 'The current results do not point to a single cause. Export the diagnostics JSON for engineering review.',
            tone: 'warning',
        });
    }

    if (interpretations.length === 0) {
        interpretations.push({
            id: 'more-data-needed',
            title: 'More data needed',
            message: 'Run the range probe and seek tests, then review the last seek result for a more specific interpretation.',
            tone: 'neutral',
        });
    }

    return interpretations;
};
